/**
 * /wiki (index) — empty state shown when no doc is selected.
 *
 * The modal-based new-doc flow is gone. The empty-state CTA
 * fires `useCreateDocAndOpen()` directly: BE creates a placeholder
 * "Untitled" row, the user is routed to `/wiki/{slug}-{tail}?focusTitle=1` and
 * the doc page auto-focuses + selects the inline title input so the
 * first keystroke replaces "Untitled". Confluence + Notion pattern.
 *
 * Phase 2 will replace this with a "last-viewed doc" auto-redirect; for
 * now the empty state is the canonical landing.
 */

import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useToast } from '@/components/ToastProvider'
import { useMe } from '@/features/me/use-me'
import { useCreateDocAndOpen } from '@/features/wiki/hooks/use-create-doc-and-open'
import { useWikiTree } from '@/features/wiki/hooks/use-wiki-tree'
import { wikiPermissionsFromMe } from '@/features/wiki/lib/permissions'

export const Route = createFileRoute('/wiki/')({
  component: DocsIndexComponent,
})

function DocsIndexComponent() {
  const { t } = useTranslation('wiki')
  const toast = useToast()
  const createAndOpen = useCreateDocAndOpen()
  const { tree, favorites, isLoading } = useWikiTree()
  // The rail's `+ New` is already gated on this; these two CTAs were
  // not, so every role without create rights — guest, commenter,
  // reader — got a button whose only outcome is a 403 and a
  // "please try again" toast no retry can satisfy.
  const me = useMe()
  const canCreate = useMemo(() => wikiPermissionsFromMe(me.data).canCreate, [me.data])

  const handleNewDoc = useCallback(async () => {
    try {
      await createAndOpen()
    } catch {
      toast.error(t('doc.createFailureToast'))
    }
  }, [createAndOpen, toast, t])

  // Tree-state branch. The empty state ("No pages yet") used to render
  // unconditionally — wrong UX when the user has pages but landed on
  // `/wiki` (e.g. after deleting the doc that was open, or hard-refresh
  // before navigating into a doc). Differentiate:
  //   - tree empty AND favorites empty → first-time empty state with CTA
  //   - tree has content → "pick from the rail" hint, still offers CTA
  const hasAnyContent = tree.length > 0 || favorites.length > 0

  if (isLoading && !hasAnyContent) {
    // Quiet skeleton while the first tree fetch lands. Avoids a flash of
    // the empty state for users who DO have pages.
    return <div className="flex h-full min-h-0 flex-1" />
  }

  if (!hasAnyContent) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center p-8">
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <h1 className="text-lg font-semibold text-fg">{t('empty.title')}</h1>
          <p className="text-sm text-fg-muted">{t('empty.body')}</p>
          {canCreate && (
            <button
              type="button"
              aria-label={t('nav.newDocAria')}
              onClick={() => {
                void handleNewDoc()
              }}
              className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-3 text-sm font-medium text-fg hover:bg-bg-muted"
            >
              {t('empty.createCta')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-8">
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <h1 className="text-lg font-semibold text-fg">{t('landing.title')}</h1>
        <p className="text-sm text-fg-muted">{t('landing.body')}</p>
        {canCreate && (
          <button
            type="button"
            aria-label={t('nav.newDocAria')}
            onClick={() => {
              void handleNewDoc()
            }}
            className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-3 text-sm font-medium text-fg hover:bg-bg-muted"
          >
            {t('empty.createCta')}
          </button>
        )}
      </div>
    </div>
  )
}
